import { UISpecCategory } from '@common/enums';
import {
  JobIcon,
  GlobeAIcon,
  GemDatasetIcon,
  ColumnIcon,
  GemCustomIcon,
  GemTransformIcon,
  GemWingIcon,
  GemIcon,
  FunctionIcon,
  PipelineIcon,
  DatabaseIcon,
  BuildingBIcon,
  GemModelIcon,
  DownloadDIcon,
  LineageIcon,
  ProjectIcon,
  GemSubgraphIcon,
  SeedTableIcon,
  TableIcon,
  ViewTableIcon,
  UserAIcon,
  UsersAIcon,
  GemAlertIcon,
  SettingsBIcon,
  FileBIcon,
  SettingsDIcon,
  ActivityIcon,
  GemRadiantIcon,
  GemDodecaIcon,
  LogoProphecyIcon,
  NewIconVariant,
  LinkDIcon,
  ClockIcon,
  GemInnerStoredProceduresIcon,
  GemSpatialIcon,
  GemPrepareIcon,
  GemParseIcon,
  PlayIcon,
  BarChartLIcon,
  AttachmentAIcon
} from '@prophecy/icons';
import { BaseColor } from '@prophecy/theme';

import { Entity } from '../types/Entity';
import { getUnknownGemName } from './unknownGem';

const UNKNOWN_GEM = getUnknownGemName();

export const EntityIconMap = {
  [Entity.SqlOrchestratorPipeline]: { icon: PipelineIcon, type: NewIconVariant.extended },
  [Entity.StoredProcedure]: { icon: GemInnerStoredProceduresIcon, type: NewIconVariant.gem },

  [Entity.Pipeline]: { icon: PipelineIcon, type: NewIconVariant.extended },
  [Entity.Spatial]: { icon: GemSpatialIcon, type: NewIconVariant.extended },
  [Entity.Prepare]: { icon: GemPrepareIcon, type: NewIconVariant.extended },
  [Entity.Transform]: { icon: GemTransformIcon, type: NewIconVariant.extended },
  [Entity.JoinSplit]: { icon: GemWingIcon, type: NewIconVariant.extended },
  [Entity.Custom]: { icon: GemCustomIcon, type: NewIconVariant.extended },
  [Entity.Parse]: { icon: GemParseIcon, type: NewIconVariant.extended },
  [Entity.Configuration]: { icon: PipelineIcon, type: NewIconVariant.extended },
  [Entity.Dataset]: { icon: GemDatasetIcon, type: NewIconVariant.extended },
  [Entity.Subgraph]: { icon: GemSubgraphIcon, type: NewIconVariant.extended },
  [Entity.Job]: { icon: JobIcon, type: NewIconVariant.extended },
  [Entity.Model]: { icon: GemModelIcon, type: NewIconVariant.extended },
  [Entity.Snapshot]: { icon: GemModelIcon, type: NewIconVariant.extended },
  [Entity.Team]: { icon: UsersAIcon, type: NewIconVariant.default },
  [Entity.User]: { icon: UserAIcon, type: NewIconVariant.default },
  [Entity.Project]: { icon: ProjectIcon, type: NewIconVariant.extended },
  [Entity.Gem]: { icon: GemIcon, type: NewIconVariant.extended },
  [Entity.Package]: { icon: BuildingBIcon, type: NewIconVariant.default },
  [Entity.Schedule]: { icon: ClockIcon, type: NewIconVariant.default },

  [Entity.Fabric]: { icon: GlobeAIcon, type: NewIconVariant.default },
  [Entity.Lineage]: { icon: LineageIcon, type: NewIconVariant.extended },
  [Entity.UDF]: { icon: FunctionIcon, type: NewIconVariant.extended },
  [Entity.Source]: { icon: GemDatasetIcon, type: NewIconVariant.extended },
  [Entity.UnreferencedSource]: { icon: GemDatasetIcon, type: NewIconVariant.extended },
  [Entity.Seed]: { icon: SeedTableIcon, type: NewIconVariant.extended },
  [Entity.Macro]: { icon: FunctionIcon, type: NewIconVariant.extended },
  PipelineConfiguration: { icon: PipelineIcon, type: NewIconVariant.extended },
  Pset: { icon: SettingsDIcon, type: NewIconVariant.default },
  PipelineConfigurations: { icon: PipelineIcon, type: NewIconVariant.extended },
  Expression: { icon: FunctionIcon, type: NewIconVariant.extended },
  Column: { icon: ColumnIcon, type: NewIconVariant.extended },
  [Entity.Function]: { icon: FunctionIcon, type: NewIconVariant.extended },
  Environment: { icon: GlobeAIcon, type: NewIconVariant.default },
  Database: { icon: DatabaseIcon, type: NewIconVariant.extended },
  [Entity.Table]: { icon: TableIcon, type: NewIconVariant.default },
  [Entity.View]: { icon: ViewTableIcon, type: NewIconVariant.extended },
  [UISpecCategory.JoinSplit]: { icon: GemWingIcon, type: NewIconVariant.extended },
  Trillion: { icon: GemAlertIcon, type: NewIconVariant.extended },
  MachineLearning: { icon: GemCustomIcon, type: NewIconVariant.extended },
  System: { icon: LogoProphecyIcon, type: NewIconVariant.default },
  [Entity.Template]: { icon: FileBIcon, type: NewIconVariant.default },
  [Entity.Transpiler]: { icon: DownloadDIcon, type: NewIconVariant.default },
  TranspilerConfig: { icon: SettingsBIcon, type: NewIconVariant.default },
  SingularDataTest: { icon: ActivityIcon, type: NewIconVariant.default },
  GenericDataTest: { icon: ActivityIcon, type: NewIconVariant.default },
  Test: { icon: ActivityIcon, type: NewIconVariant.default },
  [Entity.MarkdownDocument]: { icon: FileBIcon, type: NewIconVariant.default },
  [UNKNOWN_GEM]: { icon: GemIcon, type: NewIconVariant.extended },
  Operator: { icon: GemRadiantIcon, type: NewIconVariant.extended },
  Sensor: { icon: GemDodecaIcon, type: NewIconVariant.extended },
  SparkSQL: { icon: GemRadiantIcon, type: NewIconVariant.extended },
  TriggerNotify: { icon: GemAlertIcon, type: NewIconVariant.extended },
  Connection: { icon: LinkDIcon, type: NewIconVariant.default },
  DataTransfer: { icon: GemModelIcon, type: NewIconVariant.extended },
  [Entity.App]: { icon: BarChartLIcon, type: NewIconVariant.default },
  [Entity.Schema]: { icon: DatabaseIcon, type: NewIconVariant.extended },
  [Entity.Replay]: { icon: PlayIcon, type: NewIconVariant.default },
  [Entity.File]: { icon: FileBIcon, type: NewIconVariant.default },
  [Entity.Attachment]: { icon: AttachmentAIcon, type: NewIconVariant.default }
} as const;

export type EntityIconType = keyof typeof EntityIconMap;

export const EntityToneMap = {
  [Entity.SqlOrchestratorPipeline]: BaseColor.primary,
  [Entity.Pipeline]: BaseColor.primary,
  [Entity.Spatial]: BaseColor.grayBlue,
  [Entity.Prepare]: BaseColor.purple,
  [Entity.Transform]: BaseColor.fuchsia,
  [Entity.JoinSplit]: BaseColor.purple,
  [Entity.Custom]: BaseColor.fuchsia,
  [Entity.Parse]: BaseColor.greenLight,
  [Entity.StoredProcedure]: BaseColor.pink,
  [Entity.Transpiler]: BaseColor.grayBlue,
  [Entity.Configuration]: BaseColor.primary,
  [Entity.Dataset]: BaseColor.secondary,
  [Entity.Schema]: BaseColor.yellow,
  [Entity.Subgraph]: BaseColor.blue,
  [Entity.Job]: BaseColor.yellow,
  [Entity.Model]: BaseColor.pink,
  [Entity.Snapshot]: BaseColor.pink,
  [Entity.Team]: BaseColor.grayBlue,
  [Entity.Project]: BaseColor.grayBlue,
  [Entity.Gem]: BaseColor.blue,
  [Entity.Package]: BaseColor.orange,

  [Entity.Fabric]: BaseColor.greenLight,
  [Entity.Lineage]: BaseColor.orange,
  [Entity.Source]: BaseColor.secondary,
  [Entity.UnreferencedSource]: BaseColor.secondary,
  [Entity.Seed]: BaseColor.secondary,
  [Entity.Macro]: BaseColor.purple,
  PipelineConfiguration: BaseColor.primary,
  PipelineConfigurations: BaseColor.primary,
  Expression: BaseColor.purple,
  Column: BaseColor.blueLight,
  [Entity.Function]: BaseColor.purple,
  [Entity.UDF]: BaseColor.teal,
  Environment: BaseColor.greenLight,
  Database: BaseColor.yellow,
  [Entity.Table]: BaseColor.purple,
  [Entity.View]: BaseColor.greenLight,
  [UISpecCategory.JoinSplit]: BaseColor.purple,
  Trillion: BaseColor.orange,
  MachineLearning: BaseColor.yellow,
  TranspilerConfig: BaseColor.greenLight,
  [Entity.Template]: BaseColor.grayBlue,
  [Entity.User]: BaseColor.grayBlue,
  System: BaseColor.grayBlue,
  Pset: BaseColor.greenLight,
  SingularDataTest: BaseColor.orange,
  GenericDataTest: BaseColor.orange,
  Test: BaseColor.orange,
  Operator: BaseColor.secondary,
  Sensor: BaseColor.pink,
  SparkSQL: BaseColor.secondary,
  TriggerNotify: BaseColor.orange,
  DataTransfer: BaseColor.orange,
  [UNKNOWN_GEM]: BaseColor.blue,
  Connection: BaseColor.teal,
  [Entity.MarkdownDocument]: BaseColor.gray,
  [Entity.Schedule]: BaseColor.yellow,
  [Entity.App]: BaseColor.green,
  [Entity.ERD]: BaseColor.cyan,
  [Entity.Replay]: BaseColor.primary,
  [Entity.File]: BaseColor.grayBlue,
  [Entity.Attachment]: BaseColor.grayBlue
};

export type EntityIconKey = EntityIconType & keyof typeof EntityToneMap;
